import React, { useEffect, useRef, useState } from "react";
import { MessageCircle, Send, X } from "lucide-react";

const CHAT_API_URL = (process.env.REACT_APP_CHAT_API_URL || "").trim();
const TRAINING_LINK = "https://www.techvaseegrah.com/tktm";
const CONTACT_LINK = "/#contact";
const CONTACT_NUMBER = "+91 97864 24450";

const COMPANY_INFO = {
  journey:
    "Vijaya Mahadevan founded Vaseegrah Veda in 2017 from her kitchen in Thanjavur, Tamil Nadu, inspired by her grandmother's herbal remedies and her personal search for clean, chemical-free self-care. Starting with just Rs. 8,000, the brand now serves customers across India, Singapore, and the UAE.",
  founder:
    "Vijaya Mahadevan is the Founder & CEO of Vaseegrah Veda, based in Thanjavur, Tamil Nadu. She started the brand in 2017 from her kitchen with Rs. 8,000, inspired by her grandmother's herbal knowledge. She leads a 12-member all-women team, received the prestigious Governor's Award in 2025, and is on a mission to create 1 lakh (100,000) entrepreneurs across India.",
  location:
    "Vaseegrah Veda is headquartered in Thanjavur, Tamil Nadu, India. The brand also has a presence in Singapore and the UAE. The entrepreneur training event is held every Saturday from 9:00 AM to 2:00 PM at the Thanjavur office.",
  mission:
    "Vaseegrah Veda focuses on herbal wellness and women-led entrepreneurship, with a long-term goal of helping 100,000 entrepreneurs grow.",
  products:
    "You can ask about herbal hair care, skin care, bath powders, custom blends, diet guidance, and wellness support. All products are 100% plant-based and handmade by a team of rural women using herbs grown on their own farm.",
  training:
    "The entrepreneur program covers business registration, branding, marketing, web presence, WhatsApp and Instagram business ideas, and one-to-one consultation.",
  schedule:
    "The entrepreneur event runs every Saturday from 9:00 AM to 2:00 PM at the Thanjavur office.",
  contact:
    `Call or WhatsApp ${CONTACT_NUMBER}, or use the contact form on this page.`,
};

const SERVICE_KEYWORDS = [
  "business", "entrepreneur", "startup", "company", "vaseegrah", "veda",
  "join", "income", "earn", "mission", "vision", "journey", "founder",
  "vijaya", "mahadevan", "vijayamahadevan", "ceo", "location", "address",
  "thanjavur", "tamil", "where", "diet", "herbal", "hair", "skin", "bath",
  "product", "consultation", "training", "schedule", "contact", "whatsapp", "call",
];

const FAQ_CONTEXT = [
  COMPANY_INFO.journey, COMPANY_INFO.founder, COMPANY_INFO.location,
  COMPANY_INFO.mission, COMPANY_INFO.products,
  COMPANY_INFO.training, COMPANY_INFO.schedule, COMPANY_INFO.contact,
].join("\n");

const isServiceRelated = (query) =>
  SERVICE_KEYWORDS.some((keyword) => query.toLowerCase().includes(keyword));

const getDirectAnswer = (message) => {
  const text = message.toLowerCase();

  if (
    text.includes("vijaya") || text.includes("mahadevan") ||
    text.includes("vijayamahadevan") || text.includes("founder") ||
    text.includes("ceo") || text.includes("who started") ||
    text.includes("who founded")
  ) {
    return COMPANY_INFO.founder;
  }

  if (
    text.includes("location") || text.includes("address") ||
    text.includes("where") || text.includes("thanjavur") ||
    text.includes("office")
  ) {
    return COMPANY_INFO.location;
  }

  if (
    text.includes("journey") || text.includes("about") ||
    text.includes("company") || text.includes("vaseegrah")
  ) {
    return `${COMPANY_INFO.journey}\n\n${COMPANY_INFO.mission}`;
  }

  if (
    text.includes("join") || text.includes("earn") ||
    text.includes("business") || text.includes("startup") ||
    text.includes("training") || text.includes("entrepreneur")
  ) {
    return `Start your business journey with Vaseegrah Veda.\n\n${COMPANY_INFO.training}\n\nRegister here: ${TRAINING_LINK}`;
  }

  if (
    text.includes("product") || text.includes("herbal") ||
    text.includes("hair") || text.includes("skin") ||
    text.includes("bath") || text.includes("diet")
  ) {
    return `${COMPANY_INFO.products}\n\nFor a personalized recommendation, please contact the team directly: ${CONTACT_LINK}`;
  }

  if (
    text.includes("contact") || text.includes("phone") ||
    text.includes("call") || text.includes("whatsapp")
  ) {
    return `${COMPANY_INFO.contact}\n\nTraining: ${TRAINING_LINK}\nContact form: ${CONTACT_LINK}`;
  }

  if (
    text.includes("event") || text.includes("schedule") ||
    text.includes("time") || text.includes("saturday")
  ) {
    return `${COMPANY_INFO.schedule}\n\n${COMPANY_INFO.training}`;
  }

  if (text.includes("price") || text.includes("cost")) {
    return `Pricing depends on the product or service you need. ${COMPANY_INFO.contact}`;
  }

  return null;
};

const getFallbackAnswer = () =>
  "I can help with herbal products, diet guidance, entrepreneur training, event details, founder information, location, and contact details. Ask me about any of those topics and I will point you in the right direction.";

const getApiAnswer = async (message) => {
  if (!CHAT_API_URL) return null;
  try {
    const response = await fetch(CHAT_API_URL, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message, context: FAQ_CONTEXT }),
    });
    if (!response.ok) throw new Error(`Chat API returned ${response.status}`);
    const data = await response.json();
    const reply = typeof data.reply === "string" ? data.reply.trim() : "";
    return reply || null;
  } catch (error) {
    console.error("Chat API request failed:", error);
    return null;
  }
};

const PRIMARY = "#357859";
const PRIMARY_LIGHT = "#4a9470";

const styles = {
  toggleBtn: {
    position: "fixed",
    bottom: "24px",
    right: "24px",
    zIndex: 9999,
    width: "56px",
    height: "56px",
    borderRadius: "50%",
    background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_LIGHT} 100%)`,
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: `0 4px 20px rgba(53, 120, 89, 0.45)`,
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
    color: "#fff",
  },
  chatWindow: {
    position: "fixed",
    bottom: "90px",
    right: "24px",
    zIndex: 9998,
    width: "360px",
    maxWidth: "calc(100vw - 48px)",
    height: "520px",
    maxHeight: "calc(100vh - 120px)",
    borderRadius: "18px",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 12px 48px rgba(0,0,0,0.18)",
    animation: "vvSlideUp 0.25s ease",
    background: "#fafaf7",
  },
  header: {
    background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_LIGHT} 100%)`,
    padding: "14px 18px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    flexShrink: 0,
  },
  headerAvatar: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    background: "rgba(255,255,255,0.25)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "18px",
    flexShrink: 0,
  },
  headerText: { flex: 1 },
  headerTitle: {
    color: "#fff",
    fontWeight: "700",
    fontSize: "15px",
    margin: 0,
    lineHeight: 1.2,
    fontFamily: "'Georgia', serif",
  },
  headerSubtitle: {
    color: "rgba(255,255,255,0.8)",
    fontSize: "11px",
    margin: 0,
    marginTop: "2px",
  },
  headerClose: {
    background: "rgba(255,255,255,0.2)",
    border: "none",
    borderRadius: "50%",
    width: "28px",
    height: "28px",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    flexShrink: 0,
  },
  messagesArea: {
    flex: 1,
    overflowY: "auto",
    padding: "16px 14px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    background: "#fafaf7",
  },
  botBubble: {
    alignSelf: "flex-start",
    maxWidth: "82%",
    background: "#fff",
    border: "1px solid #e0ece6",
    borderRadius: "16px 16px 16px 4px",
    padding: "10px 14px",
    fontSize: "13.5px",
    lineHeight: "1.55",
    color: "#1a3328",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  userBubble: {
    alignSelf: "flex-end",
    maxWidth: "82%",
    background: `linear-gradient(135deg, ${PRIMARY}, ${PRIMARY_LIGHT})`,
    borderRadius: "16px 16px 4px 16px",
    padding: "10px 14px",
    fontSize: "13.5px",
    lineHeight: "1.55",
    color: "#fff",
    boxShadow: `0 1px 4px rgba(53, 120, 89, 0.25)`,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  typingBubble: {
    alignSelf: "flex-start",
    background: "#fff",
    border: "1px solid #e0ece6",
    borderRadius: "16px 16px 16px 4px",
    padding: "10px 16px",
    display: "flex",
    gap: "5px",
    alignItems: "center",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
  },
  typingDot: {
    width: "7px",
    height: "7px",
    borderRadius: "50%",
    background: PRIMARY,
    animation: "vvBounce 1.2s infinite ease-in-out",
  },
  timestamp: {
    fontSize: "10px",
    color: "#aab49e",
    marginTop: "3px",
  },
  inputRow: {
    padding: "12px 14px",
    background: "#fff",
    borderTop: "1px solid #e0ece6",
    display: "flex",
    gap: "8px",
    alignItems: "center",
    flexShrink: 0,
  },
  inputField: {
    flex: 1,
    border: `1.5px solid #b8d9c8`,
    borderRadius: "24px",
    padding: "9px 16px",
    fontSize: "13.5px",
    outline: "none",
    background: "#f2f8f5",
    color: "#1a3328",
    fontFamily: "inherit",
    transition: "border-color 0.2s",
  },
  sendBtn: {
    width: "38px",
    height: "38px",
    borderRadius: "50%",
    background: `linear-gradient(135deg, ${PRIMARY}, ${PRIMARY_LIGHT})`,
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
    flexShrink: 0,
  },
};

const Chatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([
    {
      sender: "bot",
      text: "Hi! I am the Vaseegrah Veda assistant.\nAsk me about our founder Vijaya Mahadevan, herbal products, training, location, or contact information.",
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [isTyping, messages]);

  const formatTime = (date) =>
    date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  const sendMessage = async () => {
    const trimmedInput = input.trim();
    if (!trimmedInput || isTyping) return;

    const userMessage = { sender: "user", text: trimmedInput, timestamp: new Date() };
    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsTyping(true);

    try {
      let reply = null;
      if (!isServiceRelated(trimmedInput)) {
        reply = getFallbackAnswer();
      } else {
        reply = getDirectAnswer(trimmedInput) || (await getApiAnswer(trimmedInput)) || getFallbackAnswer();
      }
      setMessages((prev) => [...prev, { sender: "bot", text: reply, timestamp: new Date() }]);
    } catch (error) {
      console.error("Chat message failed:", error);
      setMessages((prev) => [
        ...prev,
        { sender: "bot", text: "I hit a temporary issue, but I can still help with products, training, event details, and contact information.", timestamp: new Date() },
      ]);
    } finally {
      setIsTyping(false);
    }
  };

  return (
    <>
      <style>{`
        @keyframes vvSlideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes vvBounce {
          0%, 80%, 100% { transform: translateY(0); }
          40%            { transform: translateY(-6px); }
        }
        .vv-toggle:hover { transform: scale(1.08) !important; box-shadow: 0 6px 24px rgba(53,120,89,0.55) !important; }
        .vv-send:hover   { opacity: 0.82; }
        .vv-send:disabled { opacity: 0.6; cursor: not-allowed; }
        .vv-input:focus  { border-color: ${PRIMARY} !important; }
        .vv-messages::-webkit-scrollbar       { width: 4px; }
        .vv-messages::-webkit-scrollbar-track { background: transparent; }
        .vv-messages::-webkit-scrollbar-thumb { background: #b8d9c8; border-radius: 4px; }
      `}</style>

      <button
        className="vv-toggle"
        style={styles.toggleBtn}
        onClick={() => setIsOpen((open) => !open)}
        aria-label={isOpen ? "Close chat" : "Open chat"}
      >
        {isOpen ? <X size={22} /> : <MessageCircle size={22} />}
      </button>

      {isOpen && (
        <div style={styles.chatWindow}>
          <div style={styles.header}>
            <div style={styles.headerAvatar}>VV</div>
            <div style={styles.headerText}>
              <p style={styles.headerTitle}>Vaseegrah Veda</p>
              <p style={styles.headerSubtitle}>
                {CHAT_API_URL ? "Assistant with live chat support" : "Assistant with guided answers"}
              </p>
            </div>
            <button style={styles.headerClose} onClick={() => setIsOpen(false)} aria-label="Close chat">
              <X size={14} />
            </button>
          </div>

          <div className="vv-messages" style={styles.messagesArea}>
            {messages.map((message, index) => (
              <div key={`${message.sender}-${message.timestamp.getTime()}-${index}`}>
                <div style={message.sender === "bot" ? styles.botBubble : styles.userBubble}>
                  {message.text}
                </div>
                <div style={{
                  ...styles.timestamp,
                  textAlign: message.sender === "bot" ? "left" : "right",
                  paddingLeft: message.sender === "bot" ? "4px" : "0",
                  paddingRight: message.sender === "user" ? "4px" : "0",
                }}>
                  {formatTime(message.timestamp)}
                </div>
              </div>
            ))}

            {isTyping && (
              <div style={styles.typingBubble}>
                {[0, 0.2, 0.4].map((delay, index) => (
                  <div key={index} style={{ ...styles.typingDot, animationDelay: `${delay}s` }} />
                ))}
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <div style={styles.inputRow}>
            <input
              className="vv-input"
              style={styles.inputField}
              value={input}
              placeholder="Ask about herbs, founder, or location..."
              onChange={(event) => setInput(event.target.value)}
              onKeyDown={(event) => { if (event.key === "Enter") sendMessage(); }}
            />
            <button
              className="vv-send"
              style={styles.sendBtn}
              onClick={sendMessage}
              aria-label="Send message"
              disabled={isTyping}
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default Chatbot;
